import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Button } from './Button';
import { Shield } from 'lucide-react';

const CONSENT_KEY = 'cookie_consent';
const GTM_ID = 'GTM-W595WNLP';

export const CookieConsent: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    try {
      const consentStatus = localStorage.getItem(CONSENT_KEY);
      
      if (consentStatus === 'granted') {
        // User already accepted, fire the event immediately
        setIsOpen(false);
        grantConsentAndLoadGTM();
      } else {
        // No consent found, show the banner
        const timer = setTimeout(() => setIsOpen(true), 1500);
        return () => clearTimeout(timer);
      }
    } catch (e) {
      setIsOpen(true);
    }
  }, []);

  const grantConsentAndLoadGTM = () => {
    // 1. Update Google Consent Mode v2
    (window as any).dataLayer = (window as any).dataLayer || [];
    function gtag(...args: any[]){(window as any).dataLayer.push(args);}
    
    gtag('consent', 'update', {
      'ad_storage': 'granted',
      'ad_user_data': 'granted',
      'ad_personalization': 'granted',
      'analytics_storage': 'granted'
    });

    // 2. Push custom event for GTM triggers
    (window as any).dataLayer.push({
      event: 'cookie_consent_granted',
      consent: {
        marketing: true,
        analytics: true,
      },
    });

    // 3. Inject GTM script if it hasn't been injected yet
    if (!document.getElementById('gtm-script')) {
      (function(w: any, d: any, s: string, l: string, i: string) {
        w[l] = w[l] || [];
        w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
        const f = d.getElementsByTagName(s)[0];
        const j: any = d.createElement(s);
        const dl = l !== 'dataLayer' ? '&l=' + l : '';
        j.async = true;
        j.id = 'gtm-script';
        j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
        f.parentNode.insertBefore(j, f);
      })(window, document, 'script', 'dataLayer', GTM_ID);
    }
  };

  const handleAcceptAll = () => {
    localStorage.setItem(CONSENT_KEY, 'granted');
    grantConsentAndLoadGTM();
    setIsOpen(false);
  };

  const handleDecline = () => {
    localStorage.setItem(CONSENT_KEY, 'denied');
    
    // Update Google Consent Mode v2
    (window as any).dataLayer = (window as any).dataLayer || [];
    function gtag(...args: any[]){(window as any).dataLayer.push(args);}
    
    gtag('consent', 'update', {
      'ad_storage': 'denied',
      'ad_user_data': 'denied',
      'ad_personalization': 'denied',
      'analytics_storage': 'denied'
    });

    // Push custom event for GTM triggers
    (window as any).dataLayer.push({
      event: 'cookie_consent_denied',
      consent: {
        marketing: false,
        analytics: false,
      },
    });

    setIsOpen(false);
  };

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[9998] w-[calc(100%-48px)] md:w-[800px] max-w-[95vw]"
        >
          <div className="bg-zinc-900/98 backdrop-blur-3xl border border-white/10 p-4 md:py-16 md:px-10 rounded-sm shadow-[0_20px_50px_rgba(0,0,0,0.8)]">
            <div className="flex flex-col md:flex-row items-center gap-4 md:gap-8">
              <p className="text-zinc-400 text-[11px] font-light leading-relaxed flex-grow">
                We use cookies to enhance our media delivery, analyze traffic and to improve your experience. By clicking "Accept", you consent to our use of cookies.
              </p>
              <Button 
                variant="primary" 
                size="sm" 
                onClick={handleAcceptAll}
                className="w-full md:w-auto h-10 bg-brand-500 border-brand-500 text-[10px] tracking-widest px-10"
              >
                Accept
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};